import { appRoutes } from "@/app/router/appRoutes";
import { trackEvent, trackEventAsync, TrackedEvent } from "@/domains/metrics";
import type { MemAgentSlackConnectCompleteEventParams } from "@/domains/metrics/tracked-event";
import { localStorageModule } from "@/modules/local-storage";
import type { AppStore } from "@/store";

interface PendingMemAgentSlackConnectedModalStore {
  memAgent: Pick<
    AppStore["memAgent"],
    "isSlackConnected" | "reload" | "openSlackConnectedModal" | "slackWorkspaceId" | "slackTeamName"
  >;
  settingsModal?: Pick<AppStore["settingsModal"], "isOpen" | "close">;
}

interface HandlePendingMemAgentSlackConnectedModalArgs {
  pathname: string;
  store: PendingMemAgentSlackConnectedModalStore;
  maxAttempts?: number;
  retryDelayMs?: number;
}

const DEFAULT_MAX_ATTEMPTS = 6;
const DEFAULT_RETRY_DELAY_MS = 500;

const wait = (delayMs: number): Promise<void> => {
  if (delayMs <= 0) {
    return Promise.resolve();
  }

  return new Promise(resolve => {
    window.setTimeout(resolve, delayMs);
  });
};

export const handlePendingMemAgentSlackConnectedModal = async ({
  maxAttempts = DEFAULT_MAX_ATTEMPTS,
  pathname,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
  store,
}: HandlePendingMemAgentSlackConnectedModalArgs): Promise<boolean> => {
  if (pathname === appRoutes.integrationsSlackCallback({}).path) {
    return false;
  }

  const pendingContext = localStorageModule.readMemAgentSlackConnectedModalPending();
  if (!pendingContext) {
    return false;
  }

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    await store.memAgent.reload();
    if (store.memAgent.isSlackConnected) {
      localStorageModule.clearMemAgentSlackConnectedModalPending();

      if (store.settingsModal?.isOpen) {
        store.settingsModal.close();
      }

      const connectedPayload: MemAgentSlackConnectCompleteEventParams = {
        ...pendingContext,
        slack_workspace_id: store.memAgent.slackWorkspaceId,
        slack_team_name: store.memAgent.slackTeamName,
      };

      await trackEventAsync(TrackedEvent.MemAgentSlackConnectComplete, connectedPayload);
      store.memAgent.openSlackConnectedModal({ trackingContext: connectedPayload });

      return true;
    }

    if (attempt < maxAttempts) {
      await wait(retryDelayMs);
    }
  }

  localStorageModule.clearMemAgentSlackConnectedModalPending();
  trackEvent(TrackedEvent.MemAgentSlackConnectFailed, {
    ...pendingContext,
    failure_reason: "not_connected_after_reload",
  });

  return false;
};
